import { useRef, useState, useCallback } from 'react';
import type { Task } from '../../types';
import { BacklogCard } from './BacklogCard';
import { CanvasControls } from './CanvasControls';
import { useCanvasTransform } from '../../hooks/useCanvasTransform';

interface BacklogCanvasProps {
  tasks: Task[];
  onMoveToTrack: (task: Task) => void;
}

export function BacklogCanvas({ tasks, onMoveToTrack }: BacklogCanvasProps) {
  const { transform, handleWheel, resetTransform, zoomIn, zoomOut, pan } = useCanvasTransform();
  const containerRef = useRef<HTMLDivElement>(null);
  const [isPanning, setIsPanning] = useState(false);
  const panStart = useRef<{ x: number; y: number } | null>(null);

  const handleBgMouseDown = useCallback((e: React.MouseEvent) => {
    if (e.target !== e.currentTarget && !(e.target as HTMLElement).dataset.canvas) return;
    setIsPanning(true);
    panStart.current = { x: e.clientX, y: e.clientY };

    const handleMouseMove = (me: MouseEvent) => {
      if (!panStart.current) return;
      const dx = me.clientX - panStart.current.x;
      const dy = me.clientY - panStart.current.y;
      panStart.current = { x: me.clientX, y: me.clientY };
      pan(dx, dy);
    };

    const handleMouseUp = () => {
      setIsPanning(false);
      panStart.current = null;
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, [pan]);

  return (
    <div
      ref={containerRef}
      className={`relative w-full h-full overflow-hidden bg-bg-base ${isPanning ? 'cursor-grabbing' : 'cursor-default'}`}
      style={{
        backgroundImage: `
          linear-gradient(var(--color-border-subtle) 1px, transparent 1px),
          linear-gradient(90deg, var(--color-border-subtle) 1px, transparent 1px),
          linear-gradient(rgba(255,255,255,0.015) 1px, transparent 1px),
          linear-gradient(90deg, rgba(255,255,255,0.015) 1px, transparent 1px)
        `,
        backgroundSize: '96px 96px, 96px 96px, 24px 24px, 24px 24px',
      }}
      onWheel={handleWheel}
      onMouseDown={handleBgMouseDown}
      data-canvas="true"
    >
      <div
        className="absolute inset-0"
        data-canvas="true"
        style={{
          transform: `translate(${transform.panX}px, ${transform.panY}px) scale(${transform.zoom})`,
          transformOrigin: '0 0',
        }}
      >
        {tasks.map((task) => (
          <BacklogCard
            key={task.id}
            task={task}
            zoom={transform.zoom}
            onMoveToTrack={onMoveToTrack}
          />
        ))}
      </div>
      <CanvasControls
        zoom={transform.zoom}
        onZoomIn={zoomIn}
        onZoomOut={zoomOut}
        onReset={resetTransform}
      />
    </div>
  );
}
